<template>
  <div class="panel">
    <!-- Header principal -->
    <div class="w-full flex items-center justify-between mb-8">
      <h2 class="uppercase text-blue-light font-thin text-lg">
        {{ $t('project_permission_types') }}
      </h2>
      <b-button type="is-primary" @click="openCreatePermissionTypeModal" icon-pack="fas" icon-left="plus">
        {{ $t('add_generic') }}
      </b-button>
    </div>

    <!-- Tabla principal de tipos de permiso -->
    <b-table :data="permissionTypes" :loading="isLoading" detailed detail-key="id" :show-detail-icon="true" paginated
      striped bordered icon-pack="fas" :per-page="perPage" @page-change="onPageChange">
      <b-table-column field="code" :label="$t('code')" width="15%" v-slot="props">
        {{ props.row.code }}
      </b-table-column>
      <b-table-column field="name" :label="$t('name')" v-slot="props">
        {{ props.row.name }}
      </b-table-column>
      <b-table-column field="description" :label="$t('description')" v-slot="props">
        {{ props.row.description }}
      </b-table-column>
      <b-table-column label="Tareas" width="180" centered v-slot="props">
        <div class="flex items-center justify-center space-x-2">
          <span class="mr-2">{{ props.row.projects.length }}</span>
          <b-tooltip :label="$t('link_project')" position="is-left">
            <div @click.stop="openAddProjectModal(props.row)" class="cursor-pointer flex text-green ml-2">
              <b-icon pack="fas" icon="plus-circle" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('edit')" position="is-left">
            <div @click.stop="editPermissionType(props.row)" class="cursor-pointer flex text-blue ml-2">
              <b-icon pack="fas" icon="pencil-alt" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
            <div @click.stop="deletePermissionType(props.row)" class="cursor-pointer flex text-red ml-2">
              <b-icon pack="fas" icon="trash-alt" />
            </div>
          </b-tooltip>
        </div>
      </b-table-column>

      <!-- Detalle: proyectos asociados -->
      <template #detail="props">
        <div class="ml-6 mb-4">
          <h3 class="font-bold mb-2">{{ $t('linked_projects') }}</h3>
          <div v-if="props.row.projects && props.row.projects.length">
            <div
              v-for="proj in [...props.row.projects].sort((a, b) => a.name.localeCompare(b.name))"
              :key="proj.id"
              class="pl-4 py-2 border-t border-gray-200 text-gray-700 flex items-center justify-between"
            >
              <span>{{ proj.name }}</span>
              <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
                <div @click="removeProjectFromType(props.row, proj.id)" class="cursor-pointer flex text-red mr-4">
                  <b-icon pack="fas" icon="trash-alt" />
                </div>
              </b-tooltip>
            </div>
          </div>
          <div v-else class="text-center py-4">
            {{ $t('no_results') }}
          </div>
        </div>
      </template>

      <!-- Empty state tabla principal -->
      <template #empty>
        <div class="has-text-centered py-6">{{ $t('no_results') }}</div>
      </template>
    </b-table>

    <!-- Modal: Crear Tipo de Permiso -->
    <b-modal :active.sync="isCreatePermissionTypeModalActive" :width="640" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('create_permission_type') }}</p>
          <button class="delete" aria-label="close" @click="isCreatePermissionTypeModalActive = false"></button>
        </header>
        <section class="modal-card-body">
          <b-field :label="$t('code')">
            <b-input v-model="newPermissionType.code" :placeholder="$t('code')" />
          </b-field>
          <b-field :label="$t('name')">
            <b-input v-model="newPermissionType.name" :placeholder="$t('name')" />
          </b-field>
          <b-field :label="$t('description')">
            <b-input v-model="newPermissionType.description" :placeholder="$t('description')" />
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <b-button type="is-primary" @click="savePermissionType">
            {{ newPermissionType.id ? $t('update') : $t('create') }}
          </b-button>
          <b-button @click="isCreatePermissionTypeModalActive = false">{{ $t('cancel') }}</b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Modal: Asignar Proyecto a Tipo -->
    <b-modal :active.sync="isAddProjectModalActive" :width="600" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('link_project') }}</p>
          <button class="delete" aria-label="close" @click="isAddProjectModalActive = false"></button>
        </header>
        <section class="modal-card-body">
          <b-field :label="$t('project')">
            <b-select v-model="selectedProjectId" expanded :placeholder="$t('select_project_page_title')">
              <option v-for="p in availableProjects" :key="p.value" :value="p.value">
                {{ p.text }}
              </option>
            </b-select>
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <b-button type="is-primary" @click="addProjectToType">
            {{ $t('link') }}
          </b-button>
          <b-button @click="isAddProjectModalActive = false">{{ $t('cancel') }}</b-button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
export default {

  props: {
    company: { type: Object, required: true }
  },

  data() {
    return {
      permissionTypes: [],
      isLoading: false,
      perPage: 5,
      isCreatePermissionTypeModalActive: false,
      newPermissionType: { code: '', name: '', description: '' },
      isAddProjectModalActive: false,
      currentType: null,
      availableProjects: [],
      selectedProjectId: null,
    }
  },

  mounted() {
    this.fetchPermissionTypes()
  },

  methods: {

    async fetchPermissionTypes(page = 1) {
      this.isLoading = true
      try {
        const { data } = await axios.get(
          `/api/v2/company/${this.company.id}/permitting-types`,
          { params: { page, perPage: this.perPage } }
        )
        if (data.success) {
          this.permissionTypes = data.types
        }
      } catch (e) {
        this.$buefy.toast.open({ message: this.$t('task_insert_error'), type: 'is-danger' })
      } finally {
        this.isLoading = false
      }
    },

    onPageChange(page) {
      this.fetchPermissionTypes(page)
    },

    openCreatePermissionTypeModal() {
      this.isCreatePermissionTypeModalActive = true
    },

    async savePermissionType() {
      try {
        if (this.newPermissionType.id) {
          // Editar
          await axios.put(`/api/v2/permitting-type/${this.newPermissionType.id}`, {
            code: this.newPermissionType.code,
            name: this.newPermissionType.name,
            description: this.newPermissionType.description,
            company_id: this.company.id,
          });
          this.$buefy.toast.open({ message: this.$t('update_successful'), type: 'is-success' });
        } else {
          // Crear
          const { data } = await axios.post(`/api/v2/permitting-type`, {
            ...this.newPermissionType,
            company_id: this.company.id,
          });
          if (data.success) {
            this.$buefy.toast.open({ message: this.$t('task_insert_success'), type: 'is-success' });
          }
        }
        this.isCreatePermissionTypeModalActive = false;
        this.fetchPermissionTypes();
        this.newPermissionType = { code: '', name: '', description: '' };
        this.$emit('permitting-type-created');
      } catch {
        this.$buefy.toast.open({ message: this.$t('save_error'), type: 'is-danger' });
      }
    },

    openAddProjectModal(type) {
      this.currentType = type
      this.isAddProjectModalActive = true
      this.loadAvailableProjects()
    },

    async loadAvailableProjects() {
      const { data } = await axios.get('/api/v2/user/projects', {
        params: { companyId: this.company.id }
      });
      // Excluir los proyectos ya asignados a CUALQUIER permitting type de esta company
      const allLinkedIds = this.permissionTypes.flatMap(type => (type.projects || []).map(p => p.id));
      this.availableProjects = data.projects
        .filter(p => !allLinkedIds.includes(p.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(p => ({ value: p.id, text: p.name }));
    },

    async addProjectToType() {
      try {
        await axios.post(`/api/v2/permitting-types/${this.currentType.id}/assign-project`, {
          project_id: this.selectedProjectId,
        })
        this.$buefy.toast.open({ message: this.$t('save_successful'), type: 'is-success' })
        this.isAddProjectModalActive = false
        this.fetchPermissionTypes()
      } catch {
        this.$buefy.toast.open({ message: this.$t('save_error'), type: 'is-danger' })
      }
    },

    projectRowClass(row) {
      return row.active ? '' : 'opacity-50'
    },

    async removeProjectFromType(type, projectId) {
      try {
        await axios.delete(`/api/v2/permitting-types/${type.id}/project/${projectId}`);
        this.$buefy.toast.open({ message: this.$t('removed_successfully'), type: 'is-success' });
        this.fetchPermissionTypes();
      } catch {
        this.$buefy.toast.open({ message: this.$t('remove_error'), type: 'is-danger' });
      }
    },

    editPermissionType(type) {
      this.newPermissionType = {
        id: type.id,
        code: type.code,
        name: type.name,
        description: type.description
      };
      this.isCreatePermissionTypeModalActive = true;
    },

    async deletePermissionType(type) {
      try {
        this.$dialog.confirm({
          title: this.$t('remove'),
          message: this.$t('delete_confirm_text', [type.name]),
          confirmText: this.$t('remove'),
          type: 'is-danger',
          hasIcon: true,
          onConfirm: async () => {
            this.processingData = true;
            await axios.delete(`/api/v2/permitting-type/${type.id}`);
            this.$buefy.toast.open({
              message: this.$t('removed_successfully'),
              type: 'is-success'
            });
            this.fetchPermissionTypes();
            this.$emit('permitting-type-created');
          },
          onCancel: () => {
            this.processingData = false;
          }
        });
      } catch (error) {
        this.$buefy.toast.open({
          message: this.$t('remove_error') + (error.response?.data?.message || ''),
          type: 'is-danger'
        });
      } finally {
        this.processingData = false;
      }
    },

  },

}
</script>

<style scoped>
.b-table::v-deep .table td {
  vertical-align: middle;
}
</style>
